import { nasccLogo } from 'assets'
import { NavigationMenu, Dialog } from 'radix-ui'
import { Link } from 'react-router-dom'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  UserGroupIcon,
  ClipboardIcon,
  PhotoIcon,
  CursorArrowRaysIcon,
  ShieldCheckIcon,
  LifebuoyIcon,
  InformationCircleIcon,
  UsersIcon,
  GlobeAmericasIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { useState } from 'react'

const CENTERS_ITEMS = [
  {
    name: 'Member Centers',
    description: 'Map of our Member centers and their specialities.',
    href: 'member-center-map',
    icon: UserGroupIcon
  },
  {
    name: 'Associate Member Centers',
    description: 'Map of our Associate Member Centers and their specialties.',
    href: 'associate-center-map',
    icon: UserGroupIcon
  },
  {
    name: 'Center Membership Criteria',
    description: 'Elements needed to join NASCC as a member.',
    href: 'center-membership-criteria',
    icon: ClipboardIcon
  },
  {
    name: 'Member Logo Guidelines',
    description: 'Guidelines on how you can use the NASCC member logo.',
    href: 'logo-usage-guidelines',
    icon: PhotoIcon
  }
]

const RESOURCES_ITEMS = [
  // {
  //   name: 'Publications',
  //   description: 'Read about work on Sickle Cell Disease around the world.',
  //   href: 'publications',
  //   icon: ChartBarIcon
  // },
  {
    name: 'Start a Center',
    description: 'Information on how to start a Sickle Cell Center.',
    href: 'start-a-center',
    icon: CursorArrowRaysIcon
  },
  {
    name: 'Provider Resources',
    description:
      'Resources focused on providers, with links to tools to help your patients.',
    href: 'provider-resources',
    icon: ShieldCheckIcon
  },
  {
    name: 'Emergency Department Toolkit',
    description: 'Guidance and tools for emergency department care teams.',
    href: 'emergency-department-toolkit',
    icon: LifebuoyIcon
  }
  // {
  //   name: 'Job Postings',
  //   description: 'Jobs posted by our member centers around the country!',
  //   href: 'jobs',
  //   icon: ClipboardIcon
  // }
]

const ABOUT_US_ITEMS = [
  {
    name: 'About NASCC',
    description: "Information about NASCC and it's mission.",
    href: 'about-us',
    icon: InformationCircleIcon
  },
  {
    name: 'Executive Committee',
    description: "Information and biographies of NASCC's executive committee",
    href: 'executive-committee',
    icon: UsersIcon
  },
  {
    name: 'Our Partners',
    description: "Information about NASCC's partner organizations.",
    href: 'our-partners',
    icon: GlobeAmericasIcon
  }
  // {
  //   name: 'Contact Us',
  //   description: 'Contact information for NASCC',
  //   href: 'contact_info',
  //   icon: PhoneIcon
  // }
]

const NAV_ITEMS = [
  {
    name: 'Consensus Recommendations',
    href: '/consensus-recommendations/general'
  },
  {
    name: 'GRNDaD',
    href: '/grndad/about'
  },
  {
    name: 'SCD Toolkit',
    href: '/toolkit'
  },
  {
    name: 'Centers',
    href: '/centers',
    hasSubmenu: true
  },
  {
    name: 'Resources',
    href: '/resources',
    hasSubmenu: true
  },
  {
    name: 'About Us',
    href: '/about',
    hasSubmenu: true
  },
  {
    name: 'Sponsors',
    href: '/sponsors'
  }
]

const getMenuItems = (menuName: string) => {
  switch (menuName) {
    case 'Centers':
      return CENTERS_ITEMS
    case 'Resources':
      return RESOURCES_ITEMS
    case 'About Us':
      return ABOUT_US_ITEMS
    default:
      return []
  }
}

export const NavHeader: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [expandedMobileItems, setExpandedMobileItems] = useState<string[]>([])

  const toggleMobileSubmenu = (itemName: string) => {
    setExpandedMobileItems((prev) =>
      prev.includes(itemName)
        ? prev.filter((item) => item !== itemName)
        : [...prev, itemName]
    )
  }

  return (
    <>
      <NavigationMenu.Root
        className="flex justify-between items-center px-6 py-5 bg-backgroundPrimary border-b-white border-b-2"
        data-testid="nav-root"
      >
        <div data-testid="logo">
          <Link to="/">
            <img src={nasccLogo} alt="NASCC Logo" className="h-16" />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <NavigationMenu.List
          className="hidden 2xl:flex gap-6 max-w-4xl"
          data-testid="nav-links"
        >
          {NAV_ITEMS.map((item, index) => (
            <NavigationMenu.Item
              key={item.name}
              className="relative text-xl z-20"
              data-testid={`nav-item-${index}`}
            >
              {item.hasSubmenu ? (
                <>
                  <NavigationMenu.Trigger
                    className="group"
                    data-testid={`nav-trigger-${index}`}
                  >
                    <div className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors text-base leading-tight text-center">
                      <span className="whitespace-nowrap">{item.name}</span>
                      <ChevronDownIcon className="h-4 w-4 transition-transform group-hover:hidden flex-shrink-0" />
                      <ChevronUpIcon className="h-4 w-4 transition-transform hidden group-hover:block flex-shrink-0" />
                    </div>
                  </NavigationMenu.Trigger>
                  {item.hasSubmenu && (
                    <NavigationMenu.Content
                      className="absolute top-full left-0 mt-2 w-[500px] bg-[#142841] rounded-lg shadow-lg p-6"
                      data-testid={`submenu-content-${index}`}
                    >
                      <ul
                        className="grid grid-cols-2 gap-4"
                        data-testid={`submenu-items-${index}`}
                      >
                        {getMenuItems(item.name).map((menuItem, subIndex) => (
                          <li
                            key={menuItem.name}
                            data-testid={`submenu-item-${index}-${subIndex}`}
                          >
                            <NavigationMenu.Link asChild>
                              <Link
                                to={menuItem.href}
                                className="block p-3 rounded-lg hover:bg-[#1e3a5e] transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <menuItem.icon className="h-6 w-6 text-[#AF8A48] flex-shrink-0 mt-1" />
                                  <div>
                                    <div className="font-medium text-white">
                                      {menuItem.name}
                                    </div>
                                    <p className="text-sm text-[#EDE6DE]/80">
                                      {menuItem.description}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenu.Link>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenu.Content>
                  )}
                </>
              ) : (
                <NavigationMenu.Link asChild>
                  <Link
                    to={item.href}
                    className="text-gray-700 hover:text-gray-900 transition-colors text-base leading-tight text-center"
                    data-testid={`nav-link-${index}`}
                  >
                    <span className="whitespace-nowrap">{item.name}</span>
                  </Link>
                </NavigationMenu.Link>
              )}
            </NavigationMenu.Item>
          ))}
        </NavigationMenu.List>

        {/* Intermediate Navigation - Simplified for medium screens */}
        <NavigationMenu.List
          className="hidden xl:flex 2xl:hidden gap-3 max-w-2xl"
          data-testid="nav-links-medium"
        >
          {NAV_ITEMS.map((item, index) => (
            <NavigationMenu.Item
              key={item.name}
              className="relative text-lg z-20 min-w-24"
              data-testid={`nav-item-medium-${index}`}
            >
              {item.hasSubmenu ? (
                <>
                  <NavigationMenu.Trigger
                    className="group w-full"
                    data-testid={`nav-trigger-medium-${index}`}
                  >
                    <div className="flex items-start gap-1 text-gray-700 hover:text-gray-900 transition-colors text-base leading-tight justify-between max-w-36">
                      <span className="max-w-16 text-center min-h-8 flex items-center">
                        {item.name}
                      </span>
                      <div className="flex flex-col justify-center h-8">
                        <ChevronDownIcon className="h-4 w-4 transition-transform group-hover:hidden flex-shrink-0" />
                        <ChevronUpIcon className="h-4 w-4 transition-transform hidden group-hover:block flex-shrink-0" />
                      </div>
                    </div>
                  </NavigationMenu.Trigger>
                  {item.hasSubmenu && (
                    <NavigationMenu.Content
                      className="absolute top-full left-0 mt-2 w-[500px] bg-[#142841] rounded-lg shadow-lg p-6"
                      data-testid={`submenu-content-medium-${index}`}
                    >
                      <ul
                        className="grid grid-cols-2 gap-4"
                        data-testid={`submenu-items-medium-${index}`}
                      >
                        {getMenuItems(item.name).map((menuItem, subIndex) => (
                          <li
                            key={menuItem.name}
                            data-testid={`submenu-item-medium-${index}-${subIndex}`}
                          >
                            <NavigationMenu.Link asChild>
                              <Link
                                to={menuItem.href}
                                className="block p-3 rounded-lg hover:bg-[#1e3a5e] transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <menuItem.icon className="h-6 w-6 text-[#AF8A48] flex-shrink-0 mt-1" />
                                  <div>
                                    <div className="font-medium text-white">
                                      {menuItem.name}
                                    </div>
                                    <p className="text-sm text-[#EDE6DE]/80">
                                      {menuItem.description}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenu.Link>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenu.Content>
                  )}
                </>
              ) : (
                <NavigationMenu.Link asChild>
                  <Link
                    to={item.href}
                    className="text-gray-700 hover:text-gray-900 transition-colors text-base leading-tight text-center"
                    data-testid={`nav-link-medium-${index}`}
                  >
                    <span className="max-w-16 text-center min-h-8 flex items-center justify-center">
                      {item.name}
                    </span>
                  </Link>
                </NavigationMenu.Link>
              )}
            </NavigationMenu.Item>
          ))}
        </NavigationMenu.List>

        {/* Desktop Buttons */}
        <div
          data-testid="nav-buttons"
          className="hidden xl:flex gap-4 flex-shrink-0"
        >
          <Link
            to="/join"
            className="px-5 py-1.5 rounded-full bg-[#A6410A] text-white text-sm hover:bg-[#8a3608] transition-colors whitespace-nowrap"
            data-testid="become-member-button"
          >
            Become a member
          </Link>
          {/* <Link
            to="/login"
            className="px-5 py-1.5 rounded-full bg-[#3C2416] text-white text-sm hover:bg-[#2a1a10] transition-colors"
            data-testid="login-button"
          >
            Login
          </Link> */}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="xl:hidden p-2 text-gray-700 hover:text-gray-900"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-button"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </NavigationMenu.Root>

      {/* Mobile Menu Dialog */}
      <Dialog.Root open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 bg-black/50 z-40"
            data-testid="mobile-menu-overlay"
          />
          <Dialog.Content
            className="fixed inset-y-0 right-0 w-full max-w-sm bg-backgroundPrimary p-6 shadow-xl z-50"
            data-testid="mobile-menu-content"
          >
            <Dialog.Title className="sr-only">Navigation Menu</Dialog.Title>
            <Dialog.Description className="sr-only">
              Main navigation menu for the NASCC website
            </Dialog.Description>
            <div className="flex justify-between items-center mb-8">
              <img
                src={nasccLogo}
                alt="NASCC Logo"
                className="h-12"
                data-testid="mobile-logo"
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-gray-700 hover:text-gray-900"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <nav className="space-y-6" data-testid="mobile-nav">
              {NAV_ITEMS.map((item, index) => (
                <div
                  key={item.name}
                  className="space-y-2"
                  data-testid={`mobile-nav-item-${index}`}
                >
                  {item.hasSubmenu ? (
                    <>
                      <button
                        onClick={() => toggleMobileSubmenu(item.name)}
                        className="flex items-center justify-between w-full text-left text-gray-700 hover:text-gray-900"
                        data-testid={`mobile-submenu-trigger-${index}`}
                      >
                        <span className="text-lg font-medium">{item.name}</span>
                        <ChevronRightIcon
                          className={`h-5 w-5 transition-transform ${
                            expandedMobileItems.includes(item.name)
                              ? 'rotate-90'
                              : ''
                          }`}
                        />
                      </button>
                      {expandedMobileItems.includes(item.name) && (
                        <div
                          className="pl-4 space-y-4 mt-4"
                          data-testid={`mobile-submenu-content-${index}`}
                        >
                          {getMenuItems(item.name).map((menuItem, subIndex) => (
                            <Link
                              key={menuItem.name}
                              to={menuItem.href}
                              className="flex items-start gap-3 p-2 rounded-lg hover:bg-white/50 transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                              data-testid={`mobile-submenu-item-${index}-${subIndex}`}
                            >
                              <menuItem.icon className="h-6 w-6 text-[#AF8A48] flex-shrink-0 mt-1" />
                              <div>
                                <div className="font-medium text-gray-900">
                                  {menuItem.name}
                                </div>
                                <p className="text-sm text-gray-600">
                                  {menuItem.description}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="block text-lg font-medium text-gray-700 hover:text-gray-900"
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-link-${index}`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            <div className="mt-8 space-y-3" data-testid="mobile-nav-buttons">
              <Link
                to="/join"
                className="block w-full px-5 py-2 rounded-full bg-[#A6410A] text-white text-center hover:bg-[#8a3608] transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-become-member-button"
              >
                Become a member
              </Link>
              {/* <Link
                to="/login"
                className="block w-full px-5 py-2 rounded-full bg-[#3C2416] text-white text-center hover:bg-[#2a1a10] transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-login-button"
              >
                Login
              </Link> */}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
